import {
  configFields,
  configSaveResultSchema,
  configStateSchema,
  fileEntrySchema,
  hasConfigForm,
  hasPermission,
  okSchema,
  type ConfigEditor,
  type ConfigField,
  type ConfigState,
} from '@hearth/shared';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  CircleOff,
  FileWarning,
  Loader2,
  Lock,
  RotateCw,
  Save,
  Search,
  SlidersHorizontal,
  Undo2,
  Unplug,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormField } from '@/components/form-field';
import { LazyCodeEditor } from '@/components/lazy-code-editor';
import { EmptyState, SectionHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { StatusPill } from '@/components/status-badges';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest, ApiRequestError } from '@/lib/api';
import {
  editErrors,
  fieldKeyFromErrorPath,
  fieldMatches,
  shownRaw,
  withEdit,
  type ConfigEdits,
} from '@/lib/config-form';
import { languageFor } from '@/lib/files';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';
import { useServerLive } from '@/lib/server-socket';

export const Route = createFileRoute('/_app/server/$server/config')({
  validateSearch: (s: Record<string, unknown>): { editor?: string; view?: 'raw' } => ({
    ...(typeof s.editor === 'string' ? { editor: s.editor } : {}),
    ...(s.view === 'raw' ? { view: 'raw' as const } : {}),
  }),
  component: ConfigTab,
});

const configQuery = (server: string, editor: string) =>
  queryOptions({
    queryKey: ['server', server, 'config', editor],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${server}/config/${editor}`, {
        schema: configStateSchema,
        signal,
      }),
    // The game rewrites some files itself; never edit on top of a stale copy.
    staleTime: 0,
    gcTime: 0,
  });

function ConfigTab() {
  const { server: id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const editors = server.profile.config;
  const editor = editors.find((e) => e.id === search.editor) ?? editors[0];

  if (!editor)
    return (
      <EmptyState icon={CircleOff} title="No config files for this game">
        Edit the server’s files from the Files tab.
      </EmptyState>
    );
  const formAvailable = hasConfigForm(editor);
  const view = search.view === 'raw' || !formAvailable ? 'raw' : 'form';

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold tracking-tight">{editor.label}</h2>
          <p className="text-muted-foreground mt-0.5 text-sm">
            <span className="font-mono text-xs">{editor.path}</span>
            {editor.description && <> · {editor.description}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editors.length > 1 && (
            <Select
              value={editor.id}
              onValueChange={(v) => void navigate({ search: { editor: v } })}
            >
              <SelectTrigger size="sm" className="w-52" aria-label="Config file">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {editors.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {formAvailable && (
            <Tabs
              value={view}
              onValueChange={(v) =>
                void navigate({
                  search: { editor: editor.id, ...(v === 'raw' ? { view: 'raw' as const } : {}) },
                })
              }
            >
              <TabsList>
                <TabsTrigger value="form">Form</TabsTrigger>
                <TabsTrigger value="raw">Raw</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
      </div>
      {view === 'form' ? (
        <ConfigFormView key={editor.id} server={id} editor={editor} />
      ) : (
        <RawView key={editor.id} server={id} editor={editor} />
      )}
    </div>
  );
}

// --- Form ----------------------------------------------------------------------------------

function ConfigFormView({ server, editor }: { server: string; editor: ConfigEditor }) {
  const { data: srv } = useSuspenseQuery(serverQuery(server));
  const state = useQuery(configQuery(server, editor.id));
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState<ConfigEdits>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [restartFor, setRestartFor] = useState<string[] | null>(null);
  const fields = useMemo(() => configFields(editor), [editor]);
  const canWrite =
    hasPermission(srv.permissions, 'file.update') &&
    (state.data?.exists !== false || hasPermission(srv.permissions, 'file.create'));
  const dirty = Object.keys(edits).length > 0;
  const errors = { ...serverErrors, ...editErrors(fields, edits) };
  const invalid = Object.keys(editErrors(fields, edits)).length > 0;

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useMutation({
    mutationFn: (values: ConfigEdits) =>
      apiRequest(`/api/client/servers/${server}/config/${editor.id}`, {
        method: 'PUT',
        body: { values },
        schema: configSaveResultSchema,
      }),
    onSuccess: async (res) => {
      setEdits({});
      setServerErrors({});
      if (res.changed.length === 0) toast.success('Nothing to save; the file already says that.');
      else toast.success(`Saved ${editor.label.toLowerCase()}.`);
      if (res.restartRequired) {
        const labels = res.changed.map((k) => fields.find((f) => f.key === k)?.label ?? k);
        setRestartFor((prev) => [...new Set([...(prev ?? []), ...labels])]);
      }
      await queryClient.invalidateQueries({ queryKey: configQuery(server, editor.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: ['server', server, 'file', editor.path] });
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.body?.fields) {
        const mapped: Record<string, string> = {};
        for (const [path, messages] of Object.entries(e.body.fields)) {
          const key = fieldKeyFromErrorPath(path);
          if (key && messages[0]) mapped[key] = messages[0];
        }
        setServerErrors(mapped);
      }
      toast.error(errorMessage(e));
    },
  });

  if (state.isPending)
    return (
      <div className="grid gap-3" aria-busy>
        <Card className="h-40 animate-pulse" />
        <Card className="h-64 animate-pulse" />
      </div>
    );
  if (state.isError)
    return (
      <Alert variant="destructive">
        <Unplug />
        <AlertTitle>Couldn’t read {editor.path}</AlertTitle>
        <AlertDescription>{errorMessage(state.error)}</AlertDescription>
      </Alert>
    );
  const data = state.data;
  if (data.parseError)
    return (
      <Alert variant="destructive">
        <FileWarning />
        <AlertTitle>The form can’t read this file</AlertTitle>
        <AlertDescription>
          <p>{data.parseError}</p>
          <p>Fix it in the Raw view; the form comes back once the file can be read.</p>
        </AlertDescription>
      </Alert>
    );

  const change = (field: ConfigField, raw: string) => {
    setEdits((e) => withEdit(e, field, data, raw));
    setServerErrors(({ [field.key]: _, ...rest }) => rest);
  };
  const visible = editor.groups
    .map((g) => ({ ...g, fields: g.fields.filter((f) => fieldMatches(f, filter)) }))
    .filter((g) => g.fields.length > 0);

  return (
    <div className="grid gap-4">
      {restartFor && <RestartBanner server={server} labels={restartFor} />}
      {!data.exists && (
        <Alert>
          <FileWarning />
          <AlertTitle>{editor.path} doesn’t exist yet</AlertTitle>
          <AlertDescription>
            The game usually creates it on its first start. Saving here creates it with only the
            settings you change; the game fills in the rest.
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <Search
            className="text-subtle pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find a setting"
            aria-label="Find a setting"
            className="pl-8"
          />
        </div>
        {!canWrite && <StatusPill tone="muted">Read-only</StatusPill>}
      </div>

      {visible.length === 0 && (
        <p className="text-muted-foreground text-sm">No settings match “{filter}”.</p>
      )}
      {visible.map((group) => (
        <section key={group.label} aria-label={group.label}>
          <SectionHeader title={group.label} description={group.description} />
          <Card className="grid gap-5 p-4 sm:p-5 md:grid-cols-2">
            {group.fields.map((field) => (
              <ConfigFieldControl
                key={field.key}
                field={field}
                state={data}
                value={shownRaw(field, data, edits)}
                edited={field.key in edits}
                error={errors[field.key]}
                locked={data.protectedKeys.includes(field.key)}
                readOnly={!canWrite}
                onChange={(raw) => change(field, raw)}
              />
            ))}
          </Card>
        </section>
      ))}

      {data.otherKeys > 0 && (
        <p className="text-muted-foreground text-sm">
          {data.otherKeys === 1
            ? '1 other setting in this file isn’t in the form.'
            : `${data.otherKeys} other settings in this file aren’t in the form.`}{' '}
          Saving keeps {data.otherKeys === 1 ? 'it' : 'them'} exactly as{' '}
          {data.otherKeys === 1 ? 'it is' : 'they are'}; use Raw to change{' '}
          {data.otherKeys === 1 ? 'it' : 'them'}.
        </p>
      )}

      {canWrite && dirty && (
        <div
          className="bg-card shadow-card sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3"
          role="region"
          aria-label="Unsaved changes"
        >
          <p className="text-sm">
            {Object.keys(edits).length === 1
              ? '1 unsaved change'
              : `${Object.keys(edits).length} unsaved changes`}
            {invalid && <span className="text-destructive"> · fix the highlighted settings</span>}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEdits({});
                setServerErrors({});
              }}
              disabled={save.isPending}
            >
              <Undo2 /> Discard
            </Button>
            <Button
              size="sm"
              onClick={() => save.mutate(edits)}
              disabled={invalid || save.isPending}
            >
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Save changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigFieldControl({
  field,
  state,
  value,
  edited,
  error,
  locked,
  readOnly,
  onChange,
}: {
  field: ConfigField;
  state: ConfigState;
  value: string;
  edited: boolean;
  error?: string;
  locked: boolean;
  readOnly: boolean;
  onChange: (raw: string) => void;
}) {
  const missing =
    state.exists && field.type !== 'secret' && state.values[field.key] === null && !edited;
  const description = (
    <>
      {locked
        ? 'Managed by your server’s network settings; Hearth writes it on every start.'
        : field.help}
      {missing && !locked && (
        <span className="text-subtle block">
          Not in the file{field.default !== undefined ? '; the game uses the default shown' : ''}.
        </span>
      )}
    </>
  );
  const disabled = readOnly || locked;
  const label = locked ? `${field.label} (managed)` : field.label;

  if (field.type === 'boolean')
    return (
      <SwitchRow
        label={field.label}
        description={description}
        checked={value === 'true'}
        disabled={disabled}
        locked={locked}
        error={error}
        onChange={(v) => onChange(v ? 'true' : 'false')}
      />
    );

  let control;
  switch (field.type) {
    case 'select':
      control = (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            {field.options!.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label ?? o.value}
              </SelectItem>
            ))}
            {/* A value the profile doesn't list stays visible rather than silently blank. */}
            {value && !field.options!.some((o) => o.value === value) && (
              <SelectItem value={value}>{value} (not a listed option)</SelectItem>
            )}
          </SelectContent>
        </Select>
      );
      break;
    case 'multiline':
      control = (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={4}
        />
      );
      break;
    case 'secret':
      control = (
        <Input
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoComplete="new-password"
          placeholder={
            state.secretsSet.includes(field.key) ? 'Set — type a new one to replace it' : 'Not set'
          }
        />
      );
      break;
    case 'number':
      control = (
        <Input
          type="number"
          inputMode={field.integer === false ? 'decimal' : 'numeric'}
          value={value}
          min={field.min}
          max={field.max}
          step={field.integer === false ? 'any' : 1}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
      break;
    default:
      control = (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          maxLength={field.type === 'text' ? field.maxLength : undefined}
          placeholder={
            field.type === 'list' ? `Items separated by “${field.separator ?? ','}”` : ''
          }
        />
      );
  }
  return (
    <div className="grid content-start gap-1">
      <FormField label={label} error={error} description={description}>
        {control}
      </FormField>
    </div>
  );
}

function SwitchRow({
  label,
  description,
  checked,
  disabled,
  locked,
  error,
  onChange,
}: {
  label: string;
  description: ReactNode;
  checked: boolean;
  disabled: boolean;
  locked: boolean;
  error?: string;
  onChange: (v: boolean) => void;
}) {
  const id = `switch-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="grid gap-1">
        <Label htmlFor={id} className="flex items-center gap-1.5">
          {label}
          {locked && <Lock className="text-subtle size-3.5" aria-label="managed" />}
        </Label>
        <p id={`${id}-desc`} className="text-muted-foreground text-xs">
          {description}
        </p>
        {error && (
          <p className="text-destructive text-xs" role="alert">
            {error}
          </p>
        )}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        aria-describedby={`${id}-desc`}
        aria-invalid={error ? true : undefined}
      />
    </div>
  );
}

function RestartBanner({ server, labels }: { server: string; labels: string[] }) {
  const { data: srv } = useSuspenseQuery(serverQuery(server));
  const live = useServerLive(server);
  const running = live.state !== null && live.state !== 'offline';
  const canRestart = hasPermission(srv.permissions, 'control.restart');
  const restart = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/power`, {
        method: 'POST',
        body: { action: 'restart' },
        schema: okSchema,
      }),
    onSuccess: () => toast.success('Restarting…'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Alert>
      <RotateCw />
      <AlertTitle>{running ? 'Restart required' : 'Saved for the next start'}</AlertTitle>
      <AlertDescription className="grid gap-3">
        <p>
          {running
            ? 'The game only reads these settings when it starts: '
            : 'The game reads these settings when it starts: '}
          {labels.join(', ')}.
        </p>
        {running && canRestart && (
          <Button
            variant="outline"
            className="w-fit"
            onClick={() => restart.mutate()}
            disabled={restart.isPending}
          >
            {restart.isPending ? <Loader2 className="animate-spin" /> : <RotateCw />} Restart now
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

// --- Raw -----------------------------------------------------------------------------------

function RawView({ server, editor }: { server: string; editor: ConfigEditor }) {
  const { data: srv } = useSuspenseQuery(serverQuery(server));
  const queryClient = useQueryClient();
  const contents = useQuery({
    queryKey: ['server', server, 'file', editor.path],
    queryFn: async ({ signal }) => {
      try {
        return await apiRequest(
          `/api/client/servers/${server}/files/contents?file=${encodeURIComponent(editor.path)}`,
          { schema: z.string(), signal, textResponse: true },
        );
      } catch (e) {
        // A missing file opens empty and is created on save.
        if (e instanceof ApiRequestError && e.status === 404) return null;
        throw e;
      }
    },
    gcTime: 0,
  });
  const [value, setValue] = useState<string | null>(null);
  useEffect(() => {
    if (contents.data !== undefined && value === null) setValue(contents.data ?? '');
  }, [contents.data, value]);
  const exists = contents.data !== null;
  const dirty = value !== null && value !== (contents.data ?? '');
  const canWrite = hasPermission(srv.permissions, exists ? 'file.update' : 'file.create');

  const save = useMutation({
    mutationFn: (text: string) =>
      apiRequest(
        `/api/client/servers/${server}/files/write?file=${encodeURIComponent(editor.path)}&mode=${exists ? 'update' : 'create'}`,
        { method: 'POST', body: text, textBody: true, schema: fileEntrySchema },
      ),
    onSuccess: async (_entry, text) => {
      queryClient.setQueryData(['server', server, 'file', editor.path], text);
      await queryClient.invalidateQueries({ queryKey: configQuery(server, editor.id).queryKey });
      toast.success(
        editor.restartRequired
          ? `Saved ${editor.path}. Restart the server for the game to read it.`
          : `Saved ${editor.path}.`,
      );
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const doSave = () => {
    if (value !== null && canWrite && dirty && !save.isPending) save.mutate(value);
  };

  return (
    <div className="bg-card shadow-card overflow-hidden rounded-xl border">
      <div className="bg-elevated/40 flex flex-wrap items-center justify-between gap-3 border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="text-subtle size-4" aria-hidden />
          <span className="font-mono text-sm">{editor.path}</span>
          {!canWrite ? (
            <StatusPill tone="muted">Read-only</StatusPill>
          ) : save.isPending ? (
            <StatusPill tone="info" pulse>
              Saving
            </StatusPill>
          ) : dirty ? (
            <StatusPill tone="warning">Unsaved changes</StatusPill>
          ) : !exists ? (
            <StatusPill tone="muted">New file</StatusPill>
          ) : (
            <StatusPill tone="success">Saved</StatusPill>
          )}
        </div>
        <Button size="sm" onClick={doSave} disabled={!canWrite || !dirty || save.isPending}>
          {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Save
        </Button>
      </div>
      <QueryState query={contents}>
        {() => (
          <LazyCodeEditor
            value={value ?? ''}
            onChange={setValue}
            language={languageFor(editor.path)}
            onSave={doSave}
            readOnly={!canWrite}
            height="calc(100dvh - 26rem)"
            ariaLabel={`${editor.path} contents`}
            bare
          />
        )}
      </QueryState>
    </div>
  );
}
